//src/api/integrations.js
import Axios from "../config/axios-v1-config";

export const getUserIntegrations = async (values) => {
  try {
    const response = await Axios.post("integrations/verify", values);
    return response.data;
  } catch (err) {
    // status permite al consumidor distinguir "sin integraciones" (404) de un
    // fallo real (5xx/red) y no ocultar el catálogo por una cuenta nueva.
    return { error: err?.response?.data, status: err?.response?.status };
  }
};

export const getMeliAuthURL = async (values) => {
  try {
    const response = await Axios.post("integrations/mercadolibre/auth-url", values);
    return response.data;
  } catch (err) {
    return { error: err?.response?.data };
  }
};

// ─── MercadoLibre para el OPERADOR logístico ────────────────────────────────────
// El operador comparte el mismo redirect_uri de MercadoLibre que el comercio, así
// que en el kickoff se manda state="operator" y el SPA, al volver a /home/integrations,
// desvía el flujo a su callback en /admin. El token se guarda en
// logisticscompanies.integrations[] (no en companiesintegrations) y el operador se
// resuelve desde la sesión (JWT); nunca se envía su id.

// Kickoff OAuth del operador: pide la URL de autorización con state=operator.
export const getMeliOperatorAuthURL = async () => {
  try {
    const response = await Axios.post("integrations/mercadolibre/auth-url", {
      state: "operator",
    });
    return response.data; // { ok, authURL }
  } catch (err) {
    return { error: err?.response?.data || "Error desconocido" };
  }
};

// Intercambia el `code` de MercadoLibre y guarda la integración en el operador.
// Devuelve { ok, data } o { error }.
export const linkOperatorMercadoLibre = async (code) => {
  try {
    const response = await Axios.post(
      "integrations/mercadolibre/operator/generate-token",
      { code },
    );
    return response.data;
  } catch (err) {
    return { error: err?.response?.data || "Error desconocido" };
  }
};

// Jumpseller: el backend devuelve la URL de instalación OAuth a la que se debe
// redirigir al usuario. `values` = { clientId, frontendRedirectUri }.
export const getJumpsellerAuthURL = async (values) => {
  try {
    const response = await Axios.post("integrations/jumpseller/auth-url", values);
    return response.data;
  } catch (err) {
    return { error: err.response?.data || "Error desconocido" };
  }
};

// TiendaNube: mismo patrón OAuth que Jumpseller — el backend arma la URL de
// instalación (con el `state` firmado) y nosotros redirigimos el navegador.
// `values` = { clientId, frontendRedirectUri }.
export const getTiendaNubeAuthURL = async (values) => {
  try {
    const response = await Axios.post("integrations/tienda-nube/auth-url", values);
    return response.data;
  } catch (err) {
    return { error: err.response?.data || "Error desconocido" };
  }
};

// Wix: NO hay authorization-code de OAuth. El backend devuelve el "install
// link" del App Market (armado con WIX_SHARE_URL_ID) y nosotros redirigimos el
// navegador ahí; el intercambio del `signedInstance` lo resuelve entero el
// backend y después manda el navegador de vuelta al portal.
//
// `values` = { frontendRedirectUri }. NO es opcional: WixAuthUrlDto lo declara
// @IsString @IsNotEmpty @IsUrl y el MS corre con ValidationPipe global, así que
// un body vacío responde 400 antes de entrar al handler —no 503— y la conexión
// nunca arranca. El `clientId` NO se manda: el id autoritativo sale del JWT y
// mandar uno distinto al del token da 403.
//
// Se devuelve también `status` (como getUserIntegrations) porque el 503 tiene
// significado propio: falta WIX_SHARE_URL_ID en el backend, o sea que la
// integración todavía no está publicada. Sin el status, el 503 se confunde con
// un fallo transitorio y el mensaje al comercio es engañoso.
export const getWixAuthURL = async (values) => {
  try {
    const response = await Axios.post("integrations/wix/auth-url", values);
    return response.data; // { ok, authURL }
  } catch (err) {
    return {
      error: err?.response?.data || "Error desconocido",
      status: err?.response?.status,
    };
  }
};

export const addShopify = async (values) => {
  return await Axios.post(
      "integrations/shopify/add-integration",
      values
  ).catch((err) => {
    return {error: err?.response?.data};
  });
};

// ─── API pública: credenciales OAuth del comercio ───────────────────────────────
// Credenciales `client_credentials` para que el sistema del comercio (ERP, tienda
// propia) cree pedidos por API. La empresa se resuelve del JWT en el backend y
// nunca se manda. El `clientSecret` viaja UNA sola vez, en la respuesta de crear o
// de rotar: ninguna otra respuesta lo trae.
//
// Contrato de las cuatro: `{ data }` si salió bien, `{ error, status }` si no. El
// `status` va aparte porque la pantalla decide el mensaje por código (409 = tope,
// 503 al revocar = los tokens pueden seguir vivos una hora).
const apiCredentialsCall = async (request) => {
  try {
    const response = await request();
    return { data: response.data };
  } catch (err) {
    return { error: err?.response?.data, status: err?.response?.status };
  }
};

export const listApiCredentials = () =>
  apiCredentialsCall(() => Axios.get("integrations/api-credentials"));

export const createApiCredential = (values) =>
  apiCredentialsCall(() => Axios.post("integrations/api-credentials", values));

export const rotateApiCredential = (clientId) =>
  apiCredentialsCall(() =>
    Axios.post(`integrations/api-credentials/${encodeURIComponent(clientId)}/rotate`),
  );

export const revokeApiCredential = (clientId) =>
  apiCredentialsCall(() =>
    Axios.delete(`integrations/api-credentials/${encodeURIComponent(clientId)}`),
  );

// Alta de tienda Shopify por credenciales directas (vía puente, sin app pública).
// El comercio crea la app en SU Dev Dashboard y pega acá client_id y client_secret.
// La empresa se resuelve del JWT en el backend, NO se manda en el body.
// El client_secret viaja una sola vez y no vuelve nunca en la respuesta.
export const enrollShopifyDirect = async (values) => {
  return await Axios.post(
      "integrations/shopify/direct-credentials",
      values
  ).catch((err) => {
    return {error: err?.response?.data};
  });
};


// Función para integrar MercadoLibre (flex-token)
export const integrateMercadoLibre = async (values) => {
  try {
    const response = await Axios.post("integrations/mercadolibre/generate-token", values);
    return response.data;
  } catch (err) {
    return { error: err.response?.data || "Error desconocido" };
  }
};


export const addFalabella = async (values) => {
  return await Axios.post("integrations/falabella/add-integration", values).catch(
      (err) => {
        return {error: err?.response?.data};
      }
  );
};

export const addRipley = async (values) => {
  return await Axios.post("integrations/ripley/add-integration", values).catch(
      (err) => {
        return {error: err?.response?.data};
      }
  );
};

// Paris: el comercio pega la API key de su Seller Center. Devuelve
// {ok, integrationId, alias, sellerId} o {error, status} (422 key rechazada,
// 409 cuenta ya conectada a otra empresa, 503 integración no disponible).
export const connectParis = async (values) => {
  try {
    const response = await Axios.post("integrations/paris/connect", values);
    return response.data;
  } catch (err) {
    return { error: err?.response?.data, status: err?.response?.status };
  }
};

// Etiqueta de Paris de un pedido. `print` autoriza imprimirla en Paris si aún no
// se imprimió (Paris lo marca listo para despacho y el comprador ya no puede
// cancelar). Devuelve {labelUrl, carrier, trackingNumber, printedNow} o
// {error, status}; un 409 con code PARIS_LABEL_NOT_PRINTED pide confirmación.
export const getParisLabel = async (packageId, { print = false } = {}) => {
  try {
    const response = await Axios.get(
      `integrations/paris/packages/${encodeURIComponent(packageId)}/label`,
      { params: print ? { print: "true" } : undefined }
    );
    return response.data;
  } catch (err) {
    return { error: err?.response?.data, status: err?.response?.status };
  }
};

export const changeAliasApi = async (values) => {
  const response = await Axios.patch("integration", values).catch((err) => {
    return { error: err?.response?.data };
  });

  return response;
};

// Connect-flow: autoriza la vinculación de una tienda (Woo/PrestaShop) que
// llegó redirigida por su plugin; devuelve el código one-time para el canje.
export const connectAuthorize = async (platform, values) => {
  return await Axios.post(
      `integrations/${platform}/connect/authorize`,
      values
  ).catch((err) => {
    return {error: err?.response?.data};
  });
};

export const addWoocommerce = async (values) => {
  return await Axios.post(
      "integrations/woocommerce/add-integration",
      values
  ).catch((err) => {
    return {error: err?.response?.data};
  });
};

// (Re)genera la clave de conexión del plugin para una tienda WooCommerce ya
// integrada del comercio. La clave anterior queda inválida de inmediato.
export const regenerateWoocommerceKey = async (values) => {
  return await Axios.post(
      "integrations/woocommerce/connection-key",
      values
  ).catch((err) => {
    return {error: err?.response?.data};
  });
};

export const addPrestashop = async (values) => {
  return await Axios.post(
      "integrations/prestashop/add-integration",
      values
  ).catch((err) => {
    return {error: err?.response?.data};
  });
};

// (Re)genera la clave de conexión del módulo para una tienda PrestaShop ya
// integrada del comercio. La clave anterior queda inválida de inmediato.
export const regeneratePrestashopKey = async (values) => {
  return await Axios.post(
      "integrations/prestashop/connection-key",
      values
  ).catch((err) => {
    return {error: err?.response?.data};
  });
};

export const addSendu = async (values) => {
  return await Axios.post("integrations/sendu/add-integration", values).catch(
      (err) => {
        return {error: err?.response?.data};
      }
  );
};

export const deleteIntegration = async (integrationId) => {
  return await Axios.delete(`integrations/${integrationId}`).catch((err) => {
    return { error: err.response?.data };
  });
};

// Reglas de creación de pedidos de una integración (qué pedidos crean paquete en Beartrack).
export const getOrderRules = async (integrationId) => {
  try {
    const response = await Axios.get(`integrations/${integrationId}/rules`);
    return response.data; // { ok, rules }
  } catch (err) {
    return { error: err.response?.data || "Error obteniendo reglas" };
  }
};

export const updateOrderRules = async (integrationId, rules) => {
  try {
    const response = await Axios.put(`integrations/${integrationId}/rules`, rules);
    return response.data; // { ok, rules }
  } catch (err) {
    return { error: err.response?.data || "Error guardando reglas" };
  }
};

// Pedidos que las reglas filtraron (no crearon paquete), para auditoría del operador.
// Devuelve { ok, items, total, page, limit }.
export const getFilteredOrders = async ({ page = 1, limit = 20, integrationId } = {}) => {
  try {
    const response = await Axios.get("integrations/filtered-orders", {
      params: { page, limit, ...(integrationId ? { integrationId } : {}) },
    });
    return response.data;
  } catch (err) {
    return { error: err.response?.data || "Error obteniendo pedidos filtrados" };
  }
};

// Crear companiesintegrations
export const createCompanyIntegrations = async (data) => {
  try {
    const response = await Axios.post("companiesintegrations", data);
    return response.data;
  } catch (err) {
    console.error("Error in createCompanyIntegrations:", err.response?.data || 'Unknown error');
    return { error: err.response?.data || 'Unknown error' };
  }
};